import React, { useState } from "react";
import {
  Body1,
  Button,
  Caption1,
  Field,
  Input,
  Spinner,
  Subtitle2,
  Text,
} from "@fluentui/react-components";
import { EditRegular } from "@fluentui/react-icons/svg/edit";
import { LockClosed20Regular } from "@fluentui/react-icons/svg/lock-closed";
import { ShieldCheckmark20Regular } from "@fluentui/react-icons/svg/shield-checkmark";
import { WarningRegular } from "@fluentui/react-icons/svg/warning";
import { useTranslation } from "react-i18next";
import { CatalogueView } from "../../board/connections/catalogue/CatalogueView";
import { ConfigureConnectionView } from "../../board/connections/configure/ConfigureConnectionView";
import { useConnectToolsStepViewModel } from "./ConnectToolsStep.ViewModel";
import { useConnectToolsStepText } from "./ConnectToolsStep.i18n";
import FeedbackDialog from "../../feedback/FeedbackDialog";
import "../onboarding-wizard.scss";
import "../../board/connections/catalogue/AddConnectionPage.scss";
import UpgradePrompt from "@features/billing/UpgradePrompt";

interface ConnectToolsStepProps {
  onContinue: () => void;
}

export const ConnectToolsStep: React.FC<ConnectToolsStepProps> = ({
  onContinue,
}) => {
  const { t } = useTranslation();
  const text = useConnectToolsStepText(t);
  const vm = useConnectToolsStepViewModel();
  const [isSuggestSourceOpen, setIsSuggestSourceOpen] = useState(false);

  return (
    <div className="connect-tools-step">
      <div className="connect-tools-step__main">
        <div className="connect-tools-step__header">
          <Caption1 className="connect-tools-step__step-label">
            {text.stepLabelText}
          </Caption1>
          <Subtitle2>{text.titleText}</Subtitle2>
          <Body1>{text.subtitleText}</Body1>
        </div>

        {vm.isLoading ? (
          <Spinner />
        ) : vm.subPanel ? (
          <ConfigureConnectionView
            source={vm.subPanel.source}
            draftConnection={vm.subPanel.draft}
            statuses={[]}
            isConnecting={vm.isConnecting}
            onUpdate={vm.updateDraft}
            onBack={vm.closeSubPanel}
            onConnect={vm.connectSource}
            onSave={vm.closeSubPanel}
          />
        ) : vm.editingSource && vm.editingConnection ? (
          <ConfigureConnectionView
            source={vm.editingSource}
            draftConnection={vm.editingConnection}
            statuses={[]}
            isConnecting={vm.isReauthorisingEdit}
            onUpdate={vm.onUpdateEditingConnection}
            onBack={vm.onCloseEditingConnection}
            onConnect={vm.onReauthoriseEditingConnection}
            onSave={vm.onSaveEditingConnection}
            onDelete={vm.onDeleteEditingConnection}
          />
        ) : (
          <>
            <Field>
              <Input
                placeholder={text.searchPlaceholderText}
                value={vm.searchText}
                onChange={(_, data) => vm.setSearchText(data.value)}
                contentBefore={<span aria-hidden>🔍</span>}
              />
            </Field>

            <CatalogueView
              allSources={vm.allSources}
              searchText={vm.searchText}
              isConnecting={vm.isConnecting}
              hasVotedForSource={vm.hasVotedForSource}
              onSelectSource={vm.openSubPanel}
              onVoteForSource={vm.voteForSource}
              onOpenSourcePrivacy={() => undefined}
              onSuggestSource={() => setIsSuggestSourceOpen(true)}
            />
          </>
        )}
      </div>

      <aside
        className="connect-tools-step__sidebar"
        aria-label={text.sidebarHeadingText}
      >
        <ShieldCheckmark20Regular
          className="connect-tools-step__sidebar-icon"
          aria-hidden
        />
        <Text weight="semibold">{text.sidebarHeadingText}</Text>
        <ul className="connect-tools-step__sidebar-list">
          {[
            text.sidebarPoint1Text,
            text.sidebarPoint2Text,
            text.sidebarPoint3Text,
            text.sidebarPoint4Text,
          ].map((point, i) => (
            <li key={i} className="connect-tools-step__sidebar-list-item">
              <LockClosed20Regular
                className="connect-tools-step__sidebar-item-icon"
                aria-hidden
                fontSize={14}
              />
              <Caption1>{point}</Caption1>
            </li>
          ))}
        </ul>
        {vm.connections.length > 0 ? (
          <div className="connect-tools-step__connected">
            <Text weight="semibold" size={200}>
              {t("wizard.step1.connectedHeading", {
                count: vm.connections.length,
              })}
            </Text>
            <ul className="connect-tools-step__connected-list">
              {vm.connections.map((connection) => {
                const name = connection.name || connection.sourceTypeId;
                return (
                  <li
                    key={connection.id}
                    className="connect-tools-step__connected-item"
                  >
                    <img
                      className="connect-tools-step__connected-icon"
                      src={`${vm.iconBaseUrl}/assets/img/sourceicons/${connection.sourceTypeId.toLowerCase()}.png`}
                      alt=""
                      aria-hidden="true"
                    />
                    <Caption1 className="connect-tools-step__connected-name">
                      {name}
                    </Caption1>
                    {!connection.isAuthorised && (
                      <WarningRegular
                        className="connect-tools-step__connected-warning-icon"
                        aria-label={t("wizard.step1.connectedNotAuthorised", {
                          name,
                        })}
                      />
                    )}
                    <Button
                      appearance="transparent"
                      size="small"
                      icon={<EditRegular />}
                      className="connect-tools-step__connected-edit-btn"
                      aria-label={t("layout.sidebar.editConnection", { name })}
                      onClick={() => vm.onEditConnection(connection.id)}
                    />
                  </li>
                );
              })}
            </ul>
          </div>
        ) : (
          <Button appearance="subtle" size="small" onClick={onContinue}>
            {text.skipText}
          </Button>
        )}
      </aside>

      {vm.confirmDialog}
      {vm.selectOrgDialog}
      <UpgradePrompt refusal={vm.billingRefusal} onDismiss={vm.clearBillingRefusal} />

      <FeedbackDialog
        isOpen={isSuggestSourceOpen}
        onOpenChange={setIsSuggestSourceOpen}
        boardId={null}
        type="Source Suggestion"
      />
    </div>
  );
};
